import { useMemo } from "react";

import { GetExportDialogInferencePricing } from "@/components/ExportDialog/queries.graphql";
import type { InferenceParametersInput } from "@/components/graphql/schema.graphql.d";
import { DEFAULT_INFERENCE_FORM } from "@/features/forge/constants";
import { useBasicData } from "@/hooks";
import { usePreflightGated } from "@/hooks/usePreflightGated";
import { useQueryWithResponseHandler } from "@/hooks/useQueryWithResponseHandler";
import type { ActionPricing } from "@/types";

export const useExportInferencePricing = (props: {
  removeBackground?: boolean;
  batchSize: number;
}) => {
  const { removeBackground = false, batchSize } = props;
  const { workspace } = useBasicData();

  // Determines if any selected options require inference pricing
  // Add future priced options here: removeBackground || transparency || ...
  const hasBillableOptions = !!removeBackground;

  const inferenceParams = useMemo(
    (): InferenceParametersInput[] | null =>
      hasBillableOptions
        ? [
            {
              ...DEFAULT_INFERENCE_FORM.parameters,
              removeBackground,
              batchSize,
              files: [],
              styles: [],
            },
          ]
        : null,
    [removeBackground, batchSize, hasBillableOptions],
  );

  const variables = useMemo(
    () => ({
      workspaceName: workspace?.name ?? "",
      input: {
        inferenceParams: inferenceParams ?? [],
      },
    }),
    [workspace, inferenceParams],
  );

  const { data, state } = useQueryWithResponseHandler(
    {
      query: GetExportDialogInferencePricing,
      variables,
      pause: !hasBillableOptions,
    },
    {
      showSnackbarOnError: false,
    },
  );

  const pricingResult =
    hasBillableOptions && data?.workspace?.__typename === "Workspace"
      ? data.workspace.inferencePricing
      : null;
  const actionPrice = pricingResult?.actionPrice ?? null;
  const isGated = usePreflightGated(actionPrice);

  const actionPricing: ActionPricing | null = pricingResult
    ? { actionPrice, isGated }
    : null;

  return {
    data,
    state,
    actionPricing,
    isFetching: state.fetching || state.stale,
  };
};
